import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronLeft, Download, Save, BookOpen, Heart, Image, FileText, Globe, FileCode, Baby, Sun, Upload, DollarSign, AlertTriangle } from 'lucide-react';
import jsPDF from 'jspdf';

// Add this at the top of your App.js file, after the imports
const API_CONFIG = {
  backendUrl: process.env.REACT_APP_BACKEND_URL || 'https://storyapp.kodelamp.com/backend',
  frontendUrl: process.env.REACT_APP_FRONTEND_URL || 'https://storyapp.kodelamp.com',
  environment: process.env.REACT_APP_ENVIRONMENT || 'production'
};


// Log the configuration for debugging (remove in production)
if (API_CONFIG.environment === 'development') {
  console.log('API Configuration:', API_CONFIG);
}

// Constants
const steps = [
  'Story Type',      // Now step 0
  'Genre',           // Now step 1
  'Main Character',  // Now step 2
  'Setting',         // Now step 3
  'Plot Elements',   // Now step 4
  'Story Details',   // Now step 5
  'Generate Story',  // Now step 6
];


const tonesByType = {
  children: ['Playful', 'Whimsical', 'Adventurous', 'Heartwarming'],
  moral: ['Inspirational', 'Uplifting', 'Reflective', 'Hopeful'],
  general: ['Neutral', 'Dramatic', 'Humorous', 'Serious'],
};

const bookSizes = [
  { id: 'short', name: 'Short Story', description: '500 words per chapter, ideal for quick reads' },
  { id: 'medium', name: 'Medium Story', description: '1000 words per chapter, balanced narrative' },
  { id: 'long', name: 'Long Story', description: '2000 words per chapter, detailed epic' },
];

const storyTypes = [
  { id: 'children', name: 'Children\'s Story', icon: Baby, description: 'Fun and educational stories for kids' },
  { id: 'moral', name: 'Moral Story', icon: Heart, description: 'Stories with valuable life lessons' },
  { id: 'general', name: 'General Story', icon: BookOpen, description: 'Stories for all audiences' }
];

const genresByType = {
  children: [
    'Adventure', 'Animal Friends', 'Fairy Tale', 'Educational',
    'Friendship', 'Family', 'School Stories', 'Bedtime Stories', 'Custom'
  ],
  moral: [
    'Kindness & Compassion', 'Honesty & Truth', 'Courage & Bravery',
    'Friendship & Loyalty', 'Hard Work & Perseverance', 'Gratitude & Appreciation',
    'Respect & Responsibility', 'Sharing & Generosity', 'Custom'
  ],
  general: [
    'Adventure', 'Science Fiction', 'Mystery', 'Historical Fiction',
    'Contemporary Drama', 'Comedy', 'Romance', 'Custom'
  ]
};

const settingsByType = {
  children: [
    'Friendly Forest', 'Cozy Town', 'School', 'Playground',
    'Farm', 'Beach', 'Garden', 'Zoo', 'Custom'
  ],
  moral: [
    'Village', 'School', 'Home', 'Community Center',
    'Park', 'Neighborhood', 'Marketplace', 'Farm', 'Custom'
  ],
  general: [
    'Modern City', 'Small Town', 'Historical Setting', 'Countryside',
    'Beach Town', 'Mountain Village', 'Suburban Neighborhood', 'Custom'
  ]
};

const plotElementsByType = {
  children: [
    'Making new friends', 'Learning something new', 'Going on an adventure',
    'Helping others', 'Solving a problem', 'Celebrating together',
    'Discovering talents', 'Overcoming fears'
  ],
  moral: [
    'Learning from mistakes', 'Helping someone in need', 'Standing up for what\'s right',
    'The value of hard work', 'Importance of honesty', 'Power of kindness',
    'Learning to share', 'Respecting differences'
  ],
  general: [
    'Personal growth journey', 'Solving a mystery', 'Overcoming challenges',
    'Building relationships', 'Achieving goals', 'Community involvement',
    'Career development', 'Family dynamics'
  ]
};

const stylePrompts = {
  colorful: 'vibrant colorful digital illustration, highly detailed, professional artwork',
  blackwhite: 'detailed black and white illustration, grayscale, no color, high contrast',
  pencil: 'pencil sketch drawing, graphite art style, hand-drawn look, sketch lines visible',
  watercolor: 'soft watercolor painting style, artistic brushstrokes, subtle texture',
  cartoon: 'fun cartoon style, bold outlines, simplified shapes, bright colors',
  realistic: 'photorealistic, highly detailed, lifelike, 8k resolution'
};

const StoryWizardApp = () => {
  const [currentStep, setCurrentStep] = useState(0);

  const [storyData, setStoryData] = useState({
    storyType: '',
    genre: '',
    customGenre: '',
    mainCharacter: '',
    characterDetails: {
      name: '',
      age: '',
      ethnicity: '',
      appearance: '',
      personality: '',
      referenceImage: null,
      referenceImageUrl: '',
    },
    setting: '',
    customSetting: '',
    environmentDetails: '',
    plotElements: [],
    customPlotElements: '',
    tone: '',
    bookSize: 'short',
    chapters: 3,
    wordsPerChapter: 500,
    totalWords: 1500,
    generateImages: true,
    imageOrientation: 'square',
    imageSize: '1024x1024',
    imageStyle: 'colorful',
    imageQuality: 'standard',
    pdfPageSize: 'letter',
    generatedStory: null,
    generatedImages: [],
    generatedChapters: [],
    chapterSummaries: [],
    sdModel: 'sdxl',
    estimatedCost: 0,
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState('');
  const [savedStories, setSavedStories] = useState([]);
  const [showSavedStories, setShowSavedStories] = useState(false);
  const [costBreakdown, setCostBreakdown] = useState(null);

  // Load saved data from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('savedStories');
    if (saved) {
      setSavedStories(JSON.parse(saved));
    }

  }, []);

  // Update total words when book size or chapters change
  useEffect(() => {
    const wordsPerChapter = {
      'short': 500,
      'medium': 1000,
      'long': 2000
    };
    const words = wordsPerChapter[storyData.bookSize];
    setStoryData(prev => ({
      ...prev,
      wordsPerChapter: words,
      totalWords: words * prev.chapters
    }));
  }, [storyData.bookSize, storyData.chapters]);

  // Cost estimation
  useEffect(() => {
    let cost = 0;
    const breakdown = {
      storyGeneration: 0,
      imageGeneration: 0,
      total: 0,
    };

    const estimatedTokens = storyData.totalWords * 1.33;
    breakdown.storyGeneration = (estimatedTokens / 1000000) * 0.50; // Placeholder rate
    cost += breakdown.storyGeneration;

    if (storyData.generateImages) {
      const perImageCost = storyData.sdModel === 'sdxl' ? 0.01 : 0.005;
      breakdown.imageGeneration = storyData.chapters * perImageCost;
      cost += breakdown.imageGeneration;
    }

    breakdown.total = cost;
    setCostBreakdown(breakdown);
    setStoryData((prev) => ({ ...prev, estimatedCost: cost }));
  }, [storyData.totalWords, storyData.generateImages, storyData.chapters, storyData.sdModel]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setStoryData(prev => ({
          ...prev,
          characterDetails: {
            ...prev.characterDetails,
            referenceImage: file,
            referenceImageUrl: event.target.result
          }
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Enhanced chapter title extraction
  const extractChapterTitle = (chapterText) => {
    // Look for "Chapter X: Title" pattern
    const titleMatch = chapterText.match(/Chapter\s+\d+:\s*([^\n]+)/i);
    if (titleMatch && titleMatch[1]) {
      return titleMatch[1].trim();
    }

    // If no title found, extract from first sentence
    const firstSentence = chapterText.replace(/Chapter\s+\d+:?\s*/i, '').split(/[.!?]/)[0];
    if (firstSentence && firstSentence.length > 0) {
      // Limit to reasonable title length
      return firstSentence.length > 50 ? firstSentence.substring(0, 47) + '...' : firstSentence;
    }

    return 'Untitled Chapter';
  };

  const isChapterIncomplete = (chapterText) => {
    const text = chapterText.trim();

    // Check for incomplete sentences (no ending punctuation)
    const lastChar = text[text.length - 1];
    if (!['.', '!', '?', '"'].includes(lastChar)) {
      return true;
    }

    // Check if text seems too short for the target word count
    const wordCount = text.split(/\s+/).length;
    const targetWords = storyData.wordsPerChapter;
    if (wordCount < targetWords * 0.7) { // Less than 70% of target
      return true;
    }

    // Check for common incomplete endings
    const incompletePatterns = [
      /\band$/i,
      /\bthe$/i,
      /\bwhen$/i,
      /\bthen$/i,
      /\bbut$/i,
      /\bso$/i,
      /,\s*$/,
      /:\s*$/
    ];

    return incompletePatterns.some(pattern => pattern.test(text));
  };

  const continueChapter = async (incompleteChapter, chapterNumber) => {
    try {
      const continuePrompt = `Continue this story chapter from where it left off. Here's the incomplete chapter:

"${incompleteChapter}"

Please continue and complete this chapter naturally, ensuring it reaches approximately ${storyData.wordsPerChapter} words total and has a proper ending. Maintain the same tone, style, and character voice. Do not repeat what was already written - just continue from where it stopped.`;

      console.log('Continuing incomplete chapter:', chapterNumber);

      const response = await fetch(`${API_CONFIG.backendUrl}/api/openai/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: storyData.model || 'gpt-3.5-turbo',
          prompt: continuePrompt,
          max_tokens: storyData.wordsPerChapter * 0.5, // Allow up to half the original length for continuation
          temperature: 0.7
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to continue chapter: ${response.status}`);
      }

      const data = await response.json();
      const continuation = data.choices[0].text.trim();

      // Combine original and continuation
      return incompleteChapter + ' ' + continuation;

    } catch (error) {
      console.error('Error continuing chapter:', error);
      throw error;
    }
  };

  // Enhanced function to create image prompts based on actual story content
  const generateImagePromptFromStory = async (chapterText, chapterIndex) => {
    try {
      // First, extract the actual story content (remove chapter title)
      const storyContent = chapterText
        .replace(/Chapter\s+\d+:\s*[^\n]+\n?/i, '')
        .replace(/<div style="page-break-after: always;"><\/div>/g, '')
        .trim();

      // Create a prompt to analyze the story and generate an image description
      const analysisPrompt = `Read this story chapter and create a detailed visual scene description for an illustration:

"${storyContent}"

Based on this story content, describe a single key scene that would make a great illustration. Focus on:
1. What the main character is doing (specific action or pose)
2. The setting/environment details
3. Any important objects, other characters, or story elements
4. The mood/atmosphere of the scene
5. Specific visual details that capture the essence of this chapter

Respond with a concise but detailed visual description (2-3 sentences max) that an artist could use to create an illustration. Focus on the most important visual moment from this chapter.`;

      console.log('Generating image description for chapter:', chapterIndex + 1);

      // Use OpenAI to analyze the story and create a visual description
      const response = await fetch(`${API_CONFIG.backendUrl}/api/openai/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: storyData.model || 'gpt-4o-mini',
          prompt: analysisPrompt,
          max_tokens: 150,
          temperature: 0.3
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to generate image description: ${response.status}`);
      }

      const data = await response.json();
      const sceneDescription = data.choices[0].text.trim();

      console.log(`Chapter ${chapterIndex + 1} scene description:`, sceneDescription);
      return sceneDescription;

    } catch (error) {
      console.error('Error generating image prompt from story:', error);
      // Fallback to basic description if AI analysis fails
      return `${storyData.mainCharacter} in an important scene from the story`;
    }
  };

  // Updated Stable Diffusion generation with story-based prompts
  const generateWithStableDiffusion = async (chapterText, chapterIndex) => {
    try {
      setGenerationProgress(`Analyzing Chapter ${chapterIndex + 1} content for image...`);

      // Get AI-generated scene description based on actual story content
      const storySceneDescription = await generateImagePromptFromStory(chapterText, chapterIndex);

      setGenerationProgress(`Generating image for Chapter ${chapterIndex + 1}...`);

      // Build detailed character consistency prompt
      let characterPrompt = '';

      // Always include the main character type
      characterPrompt += storyData.mainCharacter;

      // Add specific appearance details for consistency
      if (storyData.characterDetails.imageAppearance) {
        characterPrompt += `, ${storyData.characterDetails.imageAppearance}`;
      }

      // Add age and ethnicity for consistency
      if (storyData.characterDetails.age) {
        characterPrompt += `, ${storyData.characterDetails.age} years old`;
      }
      if (storyData.characterDetails.ethnicity) {
        characterPrompt += `, ${storyData.characterDetails.ethnicity} ethnicity`;
      }

      // Add character name for additional consistency
      if (storyData.characterDetails.name) {
        characterPrompt += ` named ${storyData.characterDetails.name}`;
      }

      // Build the comprehensive scene prompt
      let fullScenePrompt = `${characterPrompt}, ${storySceneDescription}`;

      // Add setting context if not already mentioned in scene description
      const settingContext = storyData.setting === 'Custom' ? storyData.customSetting : storyData.setting;
      if (settingContext && !storySceneDescription.toLowerCase().includes(settingContext.toLowerCase())) {
        fullScenePrompt += `, in ${settingContext}`;
      }

      // Add environment details if provided and not redundant
      if (storyData.environmentDetails && !storySceneDescription.toLowerCase().includes(storyData.environmentDetails.toLowerCase())) {
        fullScenePrompt += `, ${storyData.environmentDetails}`;
      }

      // Add consistency and quality keywords
      fullScenePrompt += ', same character design, consistent character appearance';

      // Add story type specific elements
      if (storyData.storyType === 'children') {
        fullScenePrompt += ', child-friendly, colorful, cheerful atmosphere';
      } else if (storyData.storyType === 'moral') {
        fullScenePrompt += ', wholesome, meaningful moment';
      }

      // Combine with style prompt
      const finalPrompt = `${stylePrompts[storyData.imageStyle]}, ${fullScenePrompt}, professional illustration, detailed, high quality`;

      const negativePrompt = 'text, watermark, signature, blurry, distorted, extra limbs, deformed, bad anatomy, disfigured, low quality, artifacts, inconsistent character, different person, multiple people unless story specifies, crowd, changing appearance, different face, different hair color, different clothing style unless story specifies, generic scene, boring composition';

      console.log(`Chapter ${chapterIndex + 1} Final Image Prompt:`, finalPrompt);

      // Use character-based seed for consistency with slight variation per chapter
      const baseSeed = storyData.characterDetails.name ?
        storyData.characterDetails.name.split('').reduce((a, b) => a + b.charCodeAt(0), 0) :
        12345;

      // Add small variation for different chapters but keep base consistency
      const chapterSeed = baseSeed + (chapterIndex * 7);

      let requestBody = {};
      if (storyData.sdModel === 'sdxl') {
        requestBody = {
          version: "stability-ai/sdxl:39ed52f2a78e934b3ba6e2a89f5b1c712de7dfea535525255b1aa35c5565e08b",
          input: {
            prompt: finalPrompt,
            negative_prompt: negativePrompt,
            width: storyData.imageOrientation === 'landscape' ? 1152 : 768,
            height: storyData.imageOrientation === 'landscape' ? 768 : 1152,
            num_outputs: 1,
            guidance_scale: 12.0, // Higher for better prompt following
            num_inference_steps: 80, // More steps for quality
            refine: "expert_ensemble_refiner",
            high_noise_frac: 0.8,
            prompt_strength: 0.9, // High strength for detailed prompts
            seed: chapterSeed,
            scheduler: "DDIM"
          }
        };
      } else {
        requestBody = {
          version: "stability-ai/stable-diffusion:ac732df83cea7fff18b8472768c88ad041fa750ff7682a21affe81863cbe77e4",
          input: {
            prompt: finalPrompt,
            negative_prompt: negativePrompt,
            width: storyData.imageOrientation === 'landscape' ? 768 : 512,
            height: storyData.imageOrientation === 'landscape' ? 512 : 768,
            num_outputs: 1,
            guidance_scale: 12.0,
            num_inference_steps: 80,
            seed: chapterSeed,
            scheduler: "DDIM"
          }
        };
      }

      // Enhanced reference image handling
      if (storyData.characterDetails.referenceImage && storyData.characterDetails.referenceImageUrl) {
        requestBody.input.image = storyData.characterDetails.referenceImageUrl;
        requestBody.input.prompt_strength = 0.75; // Balanced for consistency and scene variation
        requestBody.input.prompt = `character reference image, ${finalPrompt}`;
        if (storyData.sdModel === 'sdxl') {
          requestBody.input.refine = "expert_ensemble_refiner";
          requestBody.input.high_noise_frac = 0.75;
        }
      }

      const response = await fetch(`${API_CONFIG.backendUrl}/api/replicate/predictions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        if (response.status === 402) {
          throw new Error('Replicate API error: Payment required. Please add funds to your Replicate account.');
        }
        throw new Error(`Replicate API error: ${response.status}`);
      }

      const prediction = await response.json();
      let resultUrl = prediction.urls.get;
      let finalResult = null;
      let attempts = 0;
      const maxAttempts = 60;

      while (attempts < maxAttempts) {
        const resultResponse = await fetch(resultUrl.replace('https://api.replicate.com/v1/predictions/', `${API_CONFIG.backendUrl}/api/replicate/predictions/`), {
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!resultResponse.ok) {
          if (resultResponse.status === 402) {
            throw new Error('Replicate API error: Payment required. Please add funds to your Replicate account.');
          }
          throw new Error(`Failed to get prediction status: ${resultResponse.status}`);
        }

        const statusData = await resultResponse.json();

        if (statusData.status === 'succeeded') {
          finalResult = statusData.output[0];
          break;
        } else if (statusData.status === 'failed') {
          throw new Error('Image generation failed');
        }

        attempts++;
        setGenerationProgress(`Generating image for Chapter ${chapterIndex + 1}... (${attempts * 6}s)`);
        await new Promise(resolve => setTimeout(resolve, 6000));
      }

      if (!finalResult) {
        throw new Error('Image generation timed out after 6 minutes.');
      }

      return finalResult;
    } catch (error) {
      console.error('Stable Diffusion generation error:', error);
      throw error;
    }
  };


  // Enhanced text generation with better chapter structure
  const generateText = async (prompt, chapterNumber, isLastChapter = false) => {
    try {
      const fullPrompt = `Write a ${storyData.storyType} story chapter. The main character is ${storyData.mainCharacter}. ${storyData.characterDetails.name ? `Their name is ${storyData.characterDetails.name}.` : ''
        } ${storyData.characterDetails.age ? `They are ${storyData.characterDetails.age} years old.` : ''
        } ${storyData.characterDetails.ethnicity ? `They are of ${storyData.characterDetails.ethnicity} ethnicity.` : ''
        } ${storyData.characterDetails.personality ? `Their personality includes ${storyData.characterDetails.personality}.` : ''
        } The setting is ${storyData.setting === 'Custom' ? storyData.customSetting : storyData.setting}${storyData.environmentDetails ? ` - ${storyData.environmentDetails}` : ''
        }. The genre is ${storyData.genre === 'Custom' ? storyData.customGenre : storyData.genre}. ${storyData.plotElements.length > 0 ? `Include these elements: ${storyData.plotElements.join(', ')}.` : ''
        } ${storyData.customPlotElements ? `Additional story ideas: ${storyData.customPlotElements}` : ''
        } 

    IMPORTANT: Start your response with "Chapter ${chapterNumber}: [Descriptive Title]" where the title describes what happens in this chapter.
    
    Write approximately ${storyData.wordsPerChapter} words. ${prompt}`;

      console.log('Full Prompt:', fullPrompt);

      const response = await fetch(`${API_CONFIG.backendUrl}/api/openai/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: storyData.model || 'gpt-3.5-turbo',
          prompt: fullPrompt,
          max_tokens: Math.min(storyData.wordsPerChapter * 2, 4000), // Increase max_tokens, cap at 4000
          temperature: 0.7
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`OpenAI API error: ${response.status} - ${errorData.error?.message || errorData.error || 'Unknown error'}`);
      }

      const data = await response.json();
      return data.choices[0].text.trim();
    } catch (error) {
      console.error('Text generation error:', error);
      throw error;
    }
  };

  // Enhanced function to extract key visual elements from chapter text
  const extractChapterVisuals = (chapterText, chapterIndex) => {
    const text = chapterText.toLowerCase();
    let sceneDescription = '';
    let action = '';
    let emotion = '';
    let location = '';

    // Extract actions and activities
    const actions = [
      'playing', 'running', 'jumping', 'sitting', 'standing', 'walking', 'dancing', 'singing',
      'reading', 'writing', 'drawing', 'painting', 'cooking', 'eating', 'sleeping', 'laughing',
      'crying', 'smiling', 'hugging', 'helping', 'sharing', 'learning', 'teaching', 'exploring',
      'climbing', 'swimming', 'flying', 'riding', 'driving', 'building', 'creating', 'discovering'
    ];

    // Extract emotions and expressions
    const emotions = [
      'happy', 'sad', 'excited', 'worried', 'surprised', 'confused', 'angry', 'peaceful',
      'joyful', 'curious', 'brave', 'scared', 'proud', 'shy', 'confident', 'thoughtful'
    ];

    // Extract locations and settings
    const locations = [
      'forest', 'playground', 'school', 'home', 'garden', 'park', 'beach', 'mountain',
      'library', 'store', 'farm', 'zoo', 'castle', 'village', 'city', 'room', 'kitchen',
      'bedroom', 'classroom', 'outside', 'inside', 'backyard', 'street', 'bridge'
    ];

    // Find relevant actions
    const foundActions = actions.filter(a => text.includes(a));
    if (foundActions.length > 0) {
      action = foundActions.slice(0, 2).join(' and '); // Take up to 2 actions
    }

    // Find relevant emotions
    const foundEmotions = emotions.filter(e => text.includes(e));
    if (foundEmotions.length > 0) {
      emotion = foundEmotions[0]; // Take the first emotion found
    }

    // Find relevant locations
    const foundLocations = locations.filter(l => text.includes(l));
    if (foundLocations.length > 0) {
      location = foundLocations[0];
    }

    // Build scene description based on chapter position
    if (chapterIndex === 0) {
      sceneDescription = action ? `${action} at the beginning of the story` : 'beginning of an adventure';
    } else if (action && emotion) {
      sceneDescription = `${action} while feeling ${emotion}`;
    } else if (action) {
      sceneDescription = action;
    } else if (emotion) {
      sceneDescription = `looking ${emotion}`;
    } else {
      sceneDescription = 'in an important story moment';
    }

    return {
      action,
      emotion,
      location: location || (storyData.setting === 'Custom' ? storyData.customSetting : storyData.setting),
      sceneDescription
    };
  };



  const generateStoryInChunks = async () => {
    setIsGenerating(true);
    setGenerationProgress('Generating story...');
    const chapters = [];
    const images = [];

    try {
      for (let i = 0; i < storyData.chapters; i++) {
        setGenerationProgress(`Generating Chapter ${i + 1} of ${storyData.chapters}...`);

        const chapterPrompt = `${i === 0 ? 'Begin the story with an engaging opening that introduces the character and setting. Include specific actions, dialogue, and vivid details.' :
          i === storyData.chapters - 1 ? 'Conclude the story with a satisfying ending that resolves the main conflict. Show the character in a final meaningful scene.' :
            'Continue the story with meaningful character development and plot progression. Include specific actions, interactions, and detailed scenes.'
          }`;

        let chapterText = await generateText(chapterPrompt, i + 1, i === storyData.chapters - 1);

        // Ensure chapter starts with proper title format
        if (!chapterText.match(/^Chapter\s+\d+:/i)) {
          chapterText = `Chapter ${i + 1}: ${chapterText}`;
        }

        // Check if chapter is incomplete and try to continue it
        if (isChapterIncomplete(chapterText)) {
          setGenerationProgress(`Chapter ${i + 1} seems incomplete, continuing...`);
          try {
            chapterText = await continueChapter(chapterText, i + 1);
            setGenerationProgress(`Chapter ${i + 1} continuation completed.`);
          } catch (continueError) {
            console.error(`Failed to continue chapter ${i + 1}:`, continueError);
            setGenerationProgress(`Chapter ${i + 1} continuation failed, proceeding with partial chapter...`);
            // Continue with the incomplete chapter rather than failing completely
          }
        }

        chapters.push(chapterText);

        // Generate chapter-specific image
        if (storyData.generateImages) {
          try {
            const imageUrl = await generateWithStableDiffusion(chapterText, i);
            images.push(imageUrl);
          } catch (imageError) {
            console.error(`Image generation error for Chapter ${i + 1}:`, imageError);
            images.push(null);
            setGenerationProgress(`Image generation failed for Chapter ${i + 1}: ${imageError.message}. Continuing with story...`);
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
      }

      // Final validation - check if any chapters are still incomplete
      const incompleteChapters = chapters.map((chapter, index) => ({
        index,
        incomplete: isChapterIncomplete(chapter)
      })).filter(chapter => chapter.incomplete);

      if (incompleteChapters.length > 0) {
        setGenerationProgress(`Found ${incompleteChapters.length} incomplete chapters. You can regenerate them if needed.`);

        // Store incomplete chapter info for user reference
        setStoryData(prev => ({
          ...prev,
          incompleteChapters: incompleteChapters.map(ch => ch.index + 1)
        }));
      }

      const fullStory = chapters.join('\n\n');

      setStoryData(prev => ({
        ...prev,
        generatedStory: fullStory,
        generatedChapters: chapters,
        generatedImages: images
      }));

      setGenerationProgress('Story generation complete!');
      setIsGenerating(false);

    } catch (error) {
      console.error('Story generation error:', error);
      setGenerationProgress(`Story generation failed: ${error.message}`);
      setIsGenerating(false);
    }
  };

  const regenerateChapter = async (chapterIndex) => {
    setIsGenerating(true);
    setGenerationProgress(`Regenerating Chapter ${chapterIndex + 1}...`);

    try {
      const chapterPrompt = `${chapterIndex === 0 ? 'Begin the story with an engaging opening that introduces the character and setting.' :
        chapterIndex === storyData.chapters - 1 ? 'Conclude the story with a satisfying ending that resolves the main conflict.' :
          'Continue the story with meaningful character development and plot progression.'
        }`;

      let newChapterText = await generateText(chapterPrompt, chapterIndex + 1, chapterIndex === storyData.chapters - 1);

      if (!newChapterText.match(/^Chapter\s+\d+:/i)) {
        newChapterText = `Chapter ${chapterIndex + 1}: ${newChapterText}`;
      }

      // Update the specific chapter
      const updatedChapters = [...storyData.generatedChapters];
      updatedChapters[chapterIndex] = newChapterText;

      const fullStory = updatedChapters.join('\n\n');

      setStoryData(prev => ({
        ...prev,
        generatedStory: fullStory,
        generatedChapters: updatedChapters,
        incompleteChapters: prev.incompleteChapters?.filter(ch => ch !== chapterIndex + 1) || []
      }));

      setGenerationProgress(`Chapter ${chapterIndex + 1} regenerated successfully!`);
      setIsGenerating(false);

    } catch (error) {
      console.error('Chapter regeneration error:', error);
      setGenerationProgress(`Chapter regeneration failed: ${error.message}`);
      setIsGenerating(false);
    }
  };


  const IncompleteChapterAlert = () => {
    if (!storyData.incompleteChapters || storyData.incompleteChapters.length === 0) {
      return null;
    }

    return (
      <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-yellow-800">
              Incomplete Chapters Detected
            </h4>
            <p className="text-sm text-yellow-700 mt-1">
              Chapter{storyData.incompleteChapters.length > 1 ? 's' : ''} {storyData.incompleteChapters.join(', ')}
              {storyData.incompleteChapters.length > 1 ? ' appear' : ' appears'} to be cut off due to length limits.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {storyData.incompleteChapters.map(chapterNum => (
                <button
                  key={chapterNum}
                  onClick={() => regenerateChapter(chapterNum - 1)}
                  disabled={isGenerating}
                  className="px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700 disabled:opacity-50"
                >
                  Regenerate Chapter {chapterNum}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const saveStory = () => {
    if (!storyData.generatedStory) return;
    const storyTypeName = storyTypes.find(t => t.id === storyData.storyType)?.name || 'Story';
    const newStory = {
      id: Date.now(),
      title: `${storyData.characterDetails.name || storyData.mainCharacter}'s ${storyData.genre} ${storyTypeName}`,
      date: new Date().toLocaleDateString(),
      storyData,
    };
    const updatedStories = [...savedStories, newStory];
    setSavedStories(updatedStories);
    localStorage.setItem('savedStories', JSON.stringify(updatedStories));
    alert('Story saved successfully!');
  };

  const loadStory = (story) => {
    setStoryData(story.storyData);
    setCurrentStep(7);
    setShowSavedStories(false);
  };

  const downloadStory = async (format) => {
    if (!storyData.generatedStory) return;

    const { generatedChapters, generatedImages, characterDetails, genre, storyType } = storyData;
    const storyTypeName = storyTypes.find(t => t.id === storyType)?.name || 'Story';
    const title = `${characterDetails.name || storyData.mainCharacter}'s ${genre} ${storyTypeName}`;

    // Extract chapter titles properly
    const chapterTitles = generatedChapters.map((chapter, index) => {
      return extractChapterTitle(chapter);
    });

    let content = '';

    switch (format) {
      case 'pdf':
        const pageSizes = {
          letter: { width: 215.9, height: 279.4 },
          a4: { width: 210, height: 297 },
          a5: { width: 148, height: 210 }
        };

        const pageSize = pageSizes[storyData.pdfPageSize] || pageSizes.letter;
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: [pageSize.width, pageSize.height]
        });

        let pageNumber = 1;

        const addPageNumber = () => {
          pdf.setFontSize(10);
          pdf.text(`${pageNumber}`, pageSize.width / 2, pageSize.height - 10, { align: 'center' });
          pageNumber++;
        };

        // Title page
        const titleWords = title.split(' ');
        let titleLines = [];
        let currentLine = '';

        titleWords.forEach(word => {
          const testLine = currentLine + (currentLine ? ' ' : '') + word;
          if (testLine.length > 24 || pdf.getTextWidth(testLine) > pageSize.width * 0.8) {
            if (currentLine) {
              titleLines.push(currentLine);
              currentLine = word;
            } else {
              titleLines.push(word.substring(0, 24));
              currentLine = word.substring(24);
            }
          } else {
            currentLine = testLine;
          }
        });
        if (currentLine) {
          titleLines.push(currentLine);
        }

        pdf.setFontSize(24);
        let titleY = pageSize.height * 0.35;
        titleLines.forEach(line => {
          pdf.text(line, pageSize.width / 2, titleY, { align: 'center' });
          titleY += 12;
        });

        pdf.setFontSize(16);
        pdf.text(`A ${storyTypeName}`, pageSize.width / 2, titleY + 20, { align: 'center' });
        addPageNumber();

        // Table of Contents
        pdf.addPage();
        pdf.setFontSize(20);
        pdf.setFont(undefined, 'bold');
        pdf.text('Table of Contents', pageSize.width / 2, 30, { align: 'center' });
        pdf.setFont(undefined, 'normal');

        let tocY = 50;
        pdf.setFontSize(12);
        chapterTitles.forEach((title, index) => {
          const chapterLine = `Chapter ${index + 1}: ${title}`;
          const pageNum = `${index + 3}`;

          const maxWidth = pageSize.width * 0.7;
          let displayTitle = chapterLine;
          if (pdf.getTextWidth(chapterLine) > maxWidth) {
            while (pdf.getTextWidth(displayTitle + '...') > maxWidth) {
              displayTitle = displayTitle.substring(0, displayTitle.length - 1);
            }
            displayTitle += '...';
          }

          pdf.text(displayTitle, pageSize.width * 0.15, tocY);
          pdf.text(pageNum, pageSize.width * 0.85, tocY, { align: 'right' });
          tocY += 8;
        });

        addPageNumber();

        // Chapters
        for (let i = 0; i < generatedChapters.length; i++) {
          pdf.addPage();
          let yPosition = 25;

          const chapterTitle = chapterTitles[i];

          pdf.setFontSize(18);
          pdf.setFont(undefined, 'bold');
          pdf.text(`Chapter ${i + 1}`, pageSize.width / 2, yPosition, { align: 'center' });
          yPosition += 10;

          pdf.setFontSize(14);
          pdf.setFont(undefined, 'normal');

          const chapterTitleWords = chapterTitle.split(' ');
          let chapterTitleLines = [];
          let currentTitleLine = '';

          chapterTitleWords.forEach(word => {
            const testLine = currentTitleLine + (currentTitleLine ? ' ' : '') + word;
            if (testLine.length > 30 || pdf.getTextWidth(testLine) > pageSize.width * 0.8) {
              if (currentTitleLine) {
                chapterTitleLines.push(currentTitleLine);
                currentTitleLine = word;
              } else {
                chapterTitleLines.push(word.substring(0, 30));
                currentTitleLine = word.substring(30);
              }
            } else {
              currentTitleLine = testLine;
            }
          });
          if (currentTitleLine) {
            chapterTitleLines.push(currentTitleLine);
          }

          chapterTitleLines.forEach(line => {
            pdf.text(line, pageSize.width / 2, yPosition, { align: 'center' });
            yPosition += 6;
          });

          yPosition += 10;

          pdf.setFontSize(12);
          pdf.setFont(undefined, 'normal');

          // Clean chapter text (remove title and page breaks)
          const chapterText = generatedChapters[i]
            .replace(/Chapter\s+\d+:\s*[^\n]+\n?/i, '')
            .replace(/<div style="page-break-after: always;"><\/div>/g, '')
            .trim();

          const lines = pdf.splitTextToSize(chapterText, pageSize.width * 0.8);

          const leftMargin = pageSize.width * 0.1;
          const bottomMargin = pageSize.height - 20;

          for (const line of lines) {
            if (yPosition > bottomMargin) {
              addPageNumber();
              pdf.addPage();
              yPosition = 25;
            }
            pdf.text(line, leftMargin, yPosition);
            yPosition += 6;
          }

          addPageNumber();
        }

        pdf.save(`${title}.pdf`);
        break;



      case 'html':
        content = `
  <!DOCTYPE html>
  <html>
  <head>
    <title>${title}</title>
    <meta charset="UTF-8">
    <style>
      @media print {
        .page-break { page-break-before: always; }
        .page-break-after { page-break-after: always; }
        .no-print { display: none; }
        
        /* Fixed page numbering for print */
        @page {
          margin: 1in;
          @bottom-center {
            content: counter(page);
            font-size: 12px;
            color: #666;
          }
        }
        
        /* Reset page counter for title page */
        .title-page {
          counter-reset: page 1;
        }
        
        /* Hide manual page numbers in print */
        .page-number { display: none !important; }
      }
      
      body { 
        font-family: 'Georgia', 'Times New Roman', serif; 
        max-width: 800px; 
        margin: 0 auto; 
        padding: 40px 20px;
        line-height: 1.8;
        color: #333;
        font-size: 16px;
        position: relative;
      }
      
      /* Title page styling */
      .title-page {
        min-height: 100vh;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        text-align: center;
        position: relative;
        padding-bottom: 60px; /* Space for page number */
      }
      
      h1 { 
        color: #2c3e50; 
        text-align: center;
        margin-bottom: 20px;
        font-size: 2.5em;
        font-weight: bold;
        line-height: 1.2;
      }
      
      .subtitle {
        text-align: center;
        color: #7f8c8d;
        margin-bottom: 50px;
        font-size: 1.4em;
        font-weight: normal;
      }
      
      /* Table of Contents */
      .toc {
        margin: 50px 0;
        padding: 40px;
        background: #f8f9fa;
        border-radius: 10px;
        min-height: 70vh;
        position: relative;
        padding-bottom: 80px; /* Extra space for page number */
      }
      
      .toc h2 {
        text-align: center;
        color: #2c3e50;
        margin-bottom: 40px;
        font-size: 2em;
        font-weight: bold;
      }
      
      .toc-item {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 20px;
        font-size: 1.1em;
        line-height: 1.4;
      }
      
      .toc-title {
        flex: 1;
        margin-right: 15px;
        padding-right: 10px;
      }
      
      .toc-dots {
        flex: 0 0 auto;
        width: 100px;
        border-bottom: 2px dotted #ccc;
        height: 1px;
        margin: 0 10px;
        align-self: flex-end;
        margin-bottom: 8px;
      }
      
      .toc-page {
        flex: 0 0 auto;
        font-weight: bold;
        min-width: 30px;
        text-align: right;
      }
      
      /* Chapter styling */
      .chapter { 
        margin-bottom: 60px; 
        min-height: 90vh;
        position: relative;
        padding-bottom: 80px; /* Space for page number */
        page-break-inside: avoid;
      }
      
      .chapter h2 {
        color: #34495e;
        text-align: center;
        margin-bottom: 15px;
        font-size: 2.2em;
        font-weight: bold;
      }
      
      .chapter h3 {
        text-align: center;
        color: #34495e;
        margin-bottom: 40px;
        font-size: 1.6em;
        font-weight: normal;
        font-style: italic;
      }
      
      img { 
        max-width: ${storyData.imageOrientation === 'landscape' ? '100%' : '70%'}; 
        height: auto; 
        margin: 30px auto; 
        display: block;
        border-radius: 8px;
        box-shadow: 0 4px 15px rgba(0,0,0,0.2);
      }
      
      .chapter-content {
        text-align: justify;
        font-size: 16px;
        line-height: 1.8;
        text-indent: 1.5em;
      }
      
      .chapter-content p {
        margin-bottom: 1.2em;
      }
      
      /* Fixed page number positioning */
      .page-number {
        position: absolute;
        bottom: 20px;
        left: 50%;
        transform: translateX(-50%);
        color: #95a5a6;
        font-size: 14px;
        font-family: Georgia, serif;
        z-index: 10;
      }
      
      /* Ensure page numbers don't overlap with content */
      .title-page .page-number {
        bottom: 30px;
      }
      
      .toc .page-number {
        bottom: 30px;
      }
      
      .chapter .page-number {
        bottom: 30px;
      }
      
      /* Page breaks for screen/digital viewing */
      .page-break {
        border-top: 2px dashed #ddd;
        margin: 60px 0;
        padding-top: 40px;
      }
      
      .page-break-after {
        border-bottom: 2px dashed #ddd;
        margin-bottom: 60px;
        padding-bottom: 40px;
      }
      
      /* Responsive adjustments */
      @media (max-width: 768px) {
        body {
          padding: 20px 15px;
          font-size: 14px;
        }
        
        h1 {
          font-size: 2em;
        }
        
        .chapter h2 {
          font-size: 1.8em;
        }
        
        .chapter h3 {
          font-size: 1.3em;
        }
        
        .toc {
          padding: 20px;
        }
        
        .toc-dots {
          width: 50px;
        }
      }
    </style>
  </head>
  <body>
    <!-- Title Page -->
    <div class="title-page page-break-after">
      <h1>${title}</h1>
      <p class="subtitle">A ${storyTypeName}</p>
      <div class="page-number">1</div>
    </div>
    
    <!-- Table of Contents -->
    <div class="toc page-break-after">
      <h2>Table of Contents</h2>
      ${chapterTitles.map((title, index) => `
        <div class="toc-item">
          <div class="toc-title">Chapter ${index + 1}: ${title}</div>
          <div class="toc-dots"></div>
          <div class="toc-page">${index + 3}</div>
        </div>
      `).join('')}
      <div class="page-number">2</div>
    </div>
    
    <!-- Chapters -->
    ${generatedChapters.map((chapter, index) => {
          const chapterTitle = chapterTitles[index];
          // Clean chapter content (remove title and page breaks)
          const chapterContent = chapter
            .replace(/Chapter\s+\d+:\s*[^\n]+\n?/i, '')
            .replace(/<div style="page-break-after: always;"><\/div>/g, '')
            .trim()
            .split('\n')
            .map(paragraph => paragraph.trim())
            .filter(paragraph => paragraph.length > 0)
            .map(paragraph => `<p>${paragraph}</p>`)
            .join('');

          return `
        <div class="chapter ${index < generatedChapters.length - 1 ? 'page-break-after' : ''}">
          <h2>Chapter ${index + 1}</h2>
          <h3>${chapterTitle}</h3>
          ${generatedImages[index] ?
              `<img src="${generatedImages[index]}" alt="Chapter ${index + 1} illustration" />` :
              ''
            }
          <div class="chapter-content">${chapterContent}</div>
          <div class="page-number">${index + 3}</div>
        </div>
        `;
        }).join('')}
    
    <!-- Print Instructions (hidden in print) -->
    <div class="no-print" style="margin-top: 40px; padding: 20px; background: #f0f8ff; border-radius: 8px; border-left: 4px solid #007acc;">
      <h3 style="color: #007acc; margin-top: 0;">📖 Reading & Printing Tips</h3>
      <ul style="color: #333; line-height: 1.6;">
        <li><strong>Digital Reading:</strong> Page numbers are visible for easy navigation</li>
        <li><strong>Printing:</strong> Use your browser's print function (Ctrl/Cmd + P) for best results</li>
        <li><strong>Page Breaks:</strong> Content is optimized for standard 8.5×11" paper</li>
        <li><strong>Images:</strong> High-resolution images will print clearly</li>
      </ul>
    </div>
  </body>
  </html>
`;

        const htmlBlob = new Blob([content], { type: 'text/html' });
        const htmlUrl = URL.createObjectURL(htmlBlob);
        const htmlLink = document.createElement('a');
        htmlLink.href = htmlUrl;
        htmlLink.download = `${title}.html`;
        htmlLink.click();
        URL.revokeObjectURL(htmlUrl);
        break;

      case 'txt':
        content = title + '\n' + '='.repeat(title.length) + '\n\n';
        content += 'Table of Contents\n\n';

        chapterTitles.forEach((title, index) => {
          content += `Chapter ${index + 1}: ${title}\n`;
        });

        content += '\n' + '-'.repeat(50) + '\n\n';

        // Add chapters with clean formatting
        generatedChapters.forEach((chapter, index) => {
          const chapterTitle = chapterTitles[index];
          // Clean chapter content
          const chapterContent = chapter
            .replace(/Chapter\s+\d+:\s*[^\n]+\n?/i, '')
            .replace(/<div style="page-break-after: always;"><\/div>/g, '')
            .trim();

          content += `Chapter ${index + 1}: ${chapterTitle}\n\n`;
          content += chapterContent + '\n\n';

          if (index < generatedChapters.length - 1) {
            content += '-'.repeat(30) + '\n\n';
          }
        });

        const txtBlob = new Blob([content], { type: 'text/plain' });
        const txtUrl = URL.createObjectURL(txtBlob);
        const txtLink = document.createElement('a');
        txtLink.href = txtUrl;
        txtLink.download = `${title}.txt`;
        txtLink.click();
        URL.revokeObjectURL(txtUrl);
        break;
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      // case 0:
      //   return (
      //     <div className="space-y-4">
      //       <h2 className="text-2xl font-bold mb-4">API Setup</h2>
      //       <div className="p-4 bg-yellow-50 rounded-lg mb-4">
      //         <p className="text-sm text-yellow-800">
      //           <strong>Security Warning:</strong> The OpenAI API key is stored in your browser's local storage for convenience.
      //           Avoid using this app on shared or untrusted devices to protect your key.
      //         </p>
      //       </div>
      //       <div className="space-y-6">
      //         <div>
      //           <label className="block font-semibold mb-2">OpenAI API Key</label>
      //           <input
      //             type="password"
      //             placeholder="sk-..."
      //             value={apiKey}
      //             onChange={(e) => {
      //               setApiKey(e.target.value);
      //               localStorage.setItem('openaiApiKey', e.target.value);
      //             }}
      //             className="w-full p-3 border rounded-lg"
      //             aria-label="Enter OpenAI API key"
      //           />
      //           <p className="text-sm text-gray-500 mt-1">
      //             Required for story generation. Get your key from{' '}
      //             <a
      //               href="https://platform.openai.com/api-keys"
      //               target="_blank"
      //               rel="noopener noreferrer"
      //               className="text-blue-500 underline"
      //             >
      //               OpenAI Platform
      //             </a>
      //           </p>
      //         </div>
      //         <div>
      //           <label className="block font-semibold mb-2">OpenAI Model</label>
      //           <select
      //             value={storyData.model || 'gpt-4o-mini'}
      //             onChange={(e) => setStoryData({ ...storyData, model: e.target.value })}
      //             className="w-full p-3 border rounded-lg"
      //             aria-label="Select OpenAI model"
      //           >
      //             <option value="gpt-4o-mini">GPT-3.5 Turbo</option>
      //             <option value="gpt-4">GPT-4</option>
      //             <option value="gpt-4-turbo">GPT-4 Turbo</option>
      //           </select>
      //           <p className="text-sm text-gray-500 mt-1">
      //             Choose the model for story generation. Note: Costs vary by model.
      //           </p>
      //         </div>
      //         <div className="p-4 bg-blue-50 rounded-lg">
      //           <p className="text-sm text-blue-800">
      //             The Replicate API key for image generation is managed securely on the server.
      //             Ensure your backend is configured with a valid key from{' '}
      //             <a
      //               href="https://replicate.com/account/api-tokens"
      //               target="_blank"
      //               rel="noopener noreferrer"
      //               className="text-blue-500 underline"
      //             >
      //               Replicate
      //             </a>.
      //           </p>
      //         </div>
      //       </div>
      //     </div>
      //   );
      case 0:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold mb-4">Choose Story Type</h2>
            <p className="text-gray-600 mb-4">What kind of story would you like to create?</p>
            <div className="grid gap-4">
              {storyTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    onClick={() => setStoryData({ ...storyData, storyType: type.id })}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${storyData.storyType === type.id
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'
                      }`}
                    aria-label={`Select ${type.name} story type`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="w-6 h-6 text-purple-600" />
                      <div>
                        <h3 className="font-semibold">{type.name}</h3>
                        <p className="text-sm text-gray-600">{type.description}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold mb-4">Choose Your Story Genre</h2>
            <div className="grid grid-cols-2 gap-3">
              {(genresByType[storyData.storyType] || genresByType.general).map((genre) => (
                <button
                  key={genre}
                  onClick={() => setStoryData({ ...storyData, genre })}
                  className={`p-3 rounded-lg border-2 transition-all ${storyData.genre === genre
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-purple-300'
                    }`}
                  aria-label={`Select ${genre} genre`}
                >
                  {genre}
                </button>
              ))}
            </div>
            {storyData.genre === 'Custom' && (
              <input
                type="text"
                placeholder="Enter your custom genre..."
                value={storyData.customGenre}
                onChange={(e) => setStoryData({ ...storyData, customGenre: e.target.value })}
                className="w-full p-3 border rounded-lg mt-3"
                aria-label="Enter custom genre"
              />
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold mb-4">Create Your Main Character</h2>
            {/* Primary character type/description for both text and image */}
            <input
              type="text"
              placeholder={storyData.storyType === 'children' ?
                "Character type (e.g., Curious girl with big emotions, Friendly talking dog)" :
                "Character type (e.g., Kind elderly woman, Brave firefighter man)"}
              value={storyData.mainCharacter}
              onChange={(e) => setStoryData({ ...storyData, mainCharacter: e.target.value })}
              className="w-full p-2 border rounded"
              aria-label="Enter main character type (used for story and image)"
            />
            <p className="text-sm text-gray-600">This description shapes the story and image. Include gender, age, or traits (e.g., 'Young girl with expressive face').</p>

            <div className="space-y-3 mt-4">
              <h3 className="font-semibold">Additional Character Details (Optional)</h3>
              <input
                type="text"
                placeholder="Name (e.g., Kamikam)"
                value={storyData.characterDetails.name}
                onChange={(e) => setStoryData({
                  ...storyData,
                  characterDetails: { ...storyData.characterDetails, name: e.target.value }
                })}
                className="w-full p-2 border rounded"
                aria-label="Enter character name"
              />
              <input
                type="text"
                placeholder="Age (e.g., 7, 35)"
                value={storyData.characterDetails.age}
                onChange={(e) => setStoryData({
                  ...storyData,
                  characterDetails: { ...storyData.characterDetails, age: e.target.value }
                })}
                className="w-full p-2 border rounded"
                aria-label="Enter character age"
              />
              <input
                type="text"
                placeholder="Ethnicity (e.g., African, Asian, European, Mixed)"
                value={storyData.characterDetails.ethnicity}
                onChange={(e) => setStoryData({
                  ...storyData,
                  characterDetails: { ...storyData.characterDetails, ethnicity: e.target.value }
                })}
                className="w-full p-2 border rounded"
                aria-label="Enter character ethnicity"
              />
              {/* Dedicated field for image-specific appearance */}
              <textarea
                placeholder="Image Appearance (e.g., curly hair, blue eyes, wearing a red dress)"
                value={storyData.characterDetails.imageAppearance}
                onChange={(e) => setStoryData({
                  ...storyData,
                  characterDetails: { ...storyData.characterDetails, imageAppearance: e.target.value }
                })}
                className="w-full p-2 border rounded h-20"
                aria-label="Enter character appearance for image generation"
              />
              <textarea
                placeholder="Personality traits (e.g., kind, adventurous, shy)"
                value={storyData.characterDetails.personality}
                onChange={(e) => setStoryData({
                  ...storyData,
                  characterDetails: { ...storyData.characterDetails, personality: e.target.value }
                })}
                className="w-full p-2 border rounded h-20"
                aria-label="Enter character personality"
              />
              <div>
                <label className="block font-semibold mb-1">Reference Image (Optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="w-full p-2 border rounded"
                  aria-label="Upload character reference image"
                />
                {storyData.characterDetails.referenceImageUrl && (
                  <img
                    src={storyData.characterDetails.referenceImageUrl}
                    alt="Character reference"
                    className="mt-2 w-32 h-32 object-cover rounded"
                    aria-label="Preview of uploaded character image"
                  />
                )}
              </div>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold mb-4">Select Your Setting</h2>
            <div className="grid grid-cols-2 gap-3">
              {(settingsByType[storyData.storyType] || settingsByType.general).map((setting) => (
                <button
                  key={setting}
                  onClick={() => setStoryData({ ...storyData, setting })}
                  className={`p-3 rounded-lg border-2 transition-all font-semibold ${storyData.setting === setting ?
                    'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-purple-300'
                    }`}
                  aria-label="Select setting"
                >
                  {setting}
                </button>
              ))}
            </div>
            {storyData.setting === 'Custom' && (
              <input
                type="text"
                placeholder="Enter your custom setting..."
                value={storyData.customSetting}
                onChange={(e) => setStoryData({ ...storyData, customSetting: e.target.value })}
                className="w-full p-3 border rounded-lg mt-3"
                aria-label="Enter custom setting"
              />
            )}
            <textarea
              placeholder="Describe the environment in detail (e.g., sunny playground with swings, cozy library with tall bookshelves)"
              value={storyData.environmentDetails}
              onChange={(e) => setStoryData({ ...storyData, environmentDetails: e.target.value })}
              className="w-full p-3 border rounded-lg h-24 mt-3"
              aria-label="Enter environment details"
            />
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold mb-4">Choose Plot Elements</h2>
            <p className="text-gray-600">Select elements you'd like to include in your story.</p>
            <div className="space-y-2">
              {(plotElementsByType[storyData.storyType] || plotElementsByType.general).map((element) => (
                <label key={element} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={storyData.plotElements.includes(element)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setStoryData({
                          ...storyData,
                          plotElements: [...storyData.plotElements, element]
                        });
                      } else {
                        setStoryData({
                          ...storyData,
                          plotElements: storyData.plotElements.filter(el => el !== element)
                        });
                      }
                    }}
                    className="w-4 h-4 text-purple-600"
                    aria-label="Select plot element"
                  />
                  <span>{element}</span>
                </label>
              ))}
            </div>
            <textarea
              placeholder="Add your own plot elements or story ideas..."
              value={storyData.customPlotElements}
              onChange={(e) => setStoryData({ ...storyData, customPlotElements: e.target.value })}
              className="w-full p-3 border rounded-lg h-16 mt-3"
              aria-label="Enter custom plot elements"
            />
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold mb-4">Story Details</h2>
            <div>
              <label className="block font-semibold mb-2">Story Tone</label>
              <select
                value={storyData.tone}
                onChange={(e) => setStoryData({ ...storyData, tone: e.target.value })}
                className="w-full p-3 border rounded-lg"
                aria-label="Select story tone"
              >
                <option value="">Select tone...</option>
                {(tonesByType[storyData.storyType] || tonesByType.general).map((tone) => (
                  <option key={tone} value={tone}>
                    {tone}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block font-semibold mb-2">Book Size</label>
              <div className="grid gap-3">
                {bookSizes.map((size) => (
                  <button
                    key={size.id}
                    onClick={() => setStoryData({ ...storyData, bookSize: size.id })}
                    className={`p-3 rounded-lg border-2 transition-all text-left ${storyData.bookSize === size.id
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:bg-gray-300'
                      }`}
                    aria-label={`Select ${size.name} book size`}
                  >
                    <div className="font-semibold">{size.name}</div>
                    <div className="text-sm text-gray-600">{size.description}</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block font-semibold mb-2">Number of Chapters</label>
              <input
                type="number"
                min="1"
                max="50"
                value={storyData.chapters || 3}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (value >= 1 && value <= 50) {
                    setStoryData({ ...storyData, chapters: value });
                  }
                }}
                className="w-full p-3 border rounded-lg"
                aria-label="Enter number of chapters"
              />
            </div>
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold mb-2">Story Preview</h3>
              <p className="text-sm text-gray-600">
                A {storyData.tone || 'neutral'}{' '}
                {storyData.genre === 'Custom' ? storyData.customGenre : storyData.genre} story about{' '}
                {storyData.characterDetails.name || storyData.mainCharacter} in{' '}
                {storyData.setting === 'Custom' ? storyData.customSetting : storyData.setting}.
              </p>
              <p className="text-sm text-gray-600 mt-2">
                <strong>Length:</strong> {storyData.chapters} chapters, approximately{' '}
                {storyData.totalWords} words total
              </p>
            </div>
            <div className="mt-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={storyData.generateImages}
                  onChange={(e) => setStoryData({ ...storyData, generateImages: e.target.checked })}
                  className="w-4 h-4 text-purple-600"
                  aria-label="Toggle image generation"
                />
                <span className="font-semibold">Generate illustrations for each chapter</span>
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-6">
                Uses Stable Diffusion for high-quality illustrations (requires Replicate API key)
              </p>
              {storyData.generateImages && (
                <div className="ml-6 mt-3 space-y-3">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Stable Diffusion Model</label>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setStoryData({ ...storyData, sdModel: 'sdxl' })}
                        className={`px-4 py-2 rounded border ${storyData.sdModel === 'sdxl' ? 'border-purple-600 bg-purple-50' : 'border-gray-300'
                          }`}
                        aria-label="Select SDXL model"
                      >
                        SDXL (Higher quality)
                      </button>
                      <button
                        type="button"
                        onClick={() => setStoryData({ ...storyData, sdModel: 'sd15' })}
                        className={`px-4 py-2 rounded border ${storyData.sdModel === 'sd15' ? 'border-purple-600 bg-purple-50' : 'border-gray-300'
                          }`}
                        aria-label="Select SD 1.5 model"
                      >
                        SD 1.5 (Lower cost)
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      SDXL produces higher quality images but costs ~$0.01 per image. SD 1.5 costs ~$0.005 per image.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Image Orientation</label>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() =>
                          setStoryData({
                            ...storyData,
                            imageOrientation: 'square',
                            imageSize: '1024x1024',
                          })
                        }
                        className={`px-4 py-2 rounded border ${storyData.imageOrientation === 'square'
                          ? 'border-purple-600 bg-purple-50'
                          : 'border-gray-300'
                          }`}
                        aria-label="Select square image orientation"
                      >
                        Square (1:1)
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setStoryData({
                            ...storyData,
                            imageOrientation: 'landscape',
                            imageSize: '1792x1024',
                          })
                        }
                        className={`px-4 py-2 rounded border ${storyData.imageOrientation === 'landscape'
                          ? 'border-purple-600 bg-purple-50'
                          : 'border-gray-300'
                          }`}
                        aria-label="Select landscape image orientation"
                      >
                        Landscape (16:9)
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Image Style</label>
                    <select
                      value={storyData.imageStyle}
                      onChange={(e) => setStoryData({ ...storyData, imageStyle: e.target.value })}
                      className="w-full p-2 border rounded"
                      aria-label="Select image style"
                    >
                      <option value="colorful">Colorful Digital Art</option>
                      <option value="blackwhite">Black & White</option>
                      <option value="pencil">Pencil Drawing</option>
                      <option value="watercolor">Watercolor Painting</option>
                      <option value="cartoon">Cartoon Style</option>
                      <option value="realistic">Photorealistic</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
            <div className="mt-4">
              <label className="block font-semibold mb-2">PDF Page Size</label>
              <select
                value={storyData.pdfPageSize}
                onChange={(e) => setStoryData({ ...storyData, pdfPageSize: e.target.value })}
                className="w-full p-2 border rounded"
                aria-label="Select PDF page size"
              >
                <option value="letter">Letter (8.5" × 11")</option>
                <option value="a4">A4 (210mm × 297mm)</option>
                <option value="a5">A5 (148mm × 210mm)</option>
              </select>
            </div>
            {costBreakdown ? (
              <div className="mt-6 p-4 bg-purple-50 rounded-lg">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Estimated Generation Cost
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Story generation ({storyData.model || 'gpt-4o-mini'}):</span>
                    <span>${costBreakdown.storyGeneration.toFixed(4)}</span>
                  </div>
                  {storyData.generateImages && (
                    <div className="flex justify-between">
                      <span>Image generation ({storyData.sdModel.toUpperCase()}):</span>
                      <span>${costBreakdown.imageGeneration.toFixed(4)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold border-t pt-2 mt-2">
                    <span>Total estimated cost:</span>
                    <span>${costBreakdown.total.toFixed(4)}</span>
                  </div>
                  <p className="text-xs text-purple-700 mt-2">
                    Actual cost may vary slightly based on exact token usage. You'll be billed directly by OpenAI and Replicate.
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-purple-700">Calculating estimated cost...</p>
            )}
          </div>
        );

      // Fixed story display in the generation step (case 7)
      case 6:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold mb-4">Generate Your Story</h2>
            {!storyData.generatedStory ? (
              <div className="text-center py-8">
                <Sun className="w-16 h-16 mx-auto mb-4 text-purple-500" />
                <p className="text-gray-600 mb-6">
                  Ready to create your{' '}
                  {storyData.storyType === 'children'
                    ? "children's"
                    : storyData.storyType === 'moral'
                      ? 'wholesome'
                      : ''}{' '}
                  story?
                </p>
                {generationProgress && (
                  <p className="text-purple-600 mb-4 font-semibold">{generationProgress}</p>
                )}
                <button
                  onClick={generateStoryInChunks}
                  disabled={isGenerating}
                  className="bg-purple-500 text-white px-8 py-3 rounded-lg font-semibold disabled:bg-gray-300 hover:bg-purple-600 transition-colors"
                  aria-label="Generate story"
                >
                  {isGenerating ? 'Creating Your Story...' : 'Generate Story'}
                </button>
              </div>
            ) : (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold">Your Story</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={saveStory}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                      aria-label="Save story"
                    >
                      <Save className="w-4 h-4" />
                      Save
                    </button>
                  </div>
                </div>
                <div className="prose max-w-none bg-white p-6 rounded-lg border">
                  {storyData.generatedChapters.map((chapter, index) => {
                    // Extract chapter title for display
                    const chapterTitle = extractChapterTitle(chapter);

                    // Clean chapter content (remove title and page breaks for display)
                    const chapterContent = chapter
                      .replace(/Chapter\s+\d+:\s*[^\n]+\n?/i, '')
                      .replace(/<div style="page-break-after: always;"><\/div>/g, '')
                      .trim();

                    return (
                      <div key={index} className="mb-8 pb-6 border-b border-gray-200 last:border-b-0">
                        <div className="text-center mb-4">
                          <h4 className="text-xl font-bold text-purple-700 mb-2">
                            Chapter {index + 1}
                          </h4>
                          <h5 className="text-lg font-medium text-gray-700 italic">
                            {chapterTitle}
                          </h5>
                        </div>

                        {storyData.generatedImages[index] && (
                          <img
                            src={storyData.generatedImages[index]}
                            alt={`Chapter ${index + 1} illustration: ${chapterTitle}`}
                            className="w-full max-w-md mx-auto my-6 rounded-lg"
                          />
                        )}

                        <div className="text-gray-800 leading-relaxed whitespace-pre-wrap text-justify">
                          {chapterContent}
                        </div>
                      </div>
                    );
                  })}
                  <IncompleteChapterAlert />
                </div>
                <div className="mt-6 flex flex-wrap gap-3">
                  <button
                    onClick={() => downloadStory('html')}
                    className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                    aria-label="Download story as HTML"
                  >
                    <Globe className="w-4 h-4" />
                    Download HTML
                  </button>
                  <button
                    onClick={() => downloadStory('pdf')}
                    className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                    aria-label="Download story as PDF"
                  >
                    <FileText className="w-4 h-4" />
                    Download PDF
                  </button>
                  <button
                    onClick={() => downloadStory('txt')}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                    aria-label="Download story as TXT"
                  >
                    <FileCode className="w-4 h-4" />
                    Download TXT
                  </button>
                  {storyData.generatedImages.some((img) => img) && (
                    <button
                      onClick={() => {
                        storyData.generatedImages.forEach((img, index) => {
                          if (img) {
                            const link = document.createElement('a');
                            link.href = img;
                            link.download = `chapter-${index + 1}-illustration.png`;
                            link.target = '_blank';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }
                        });
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600"
                      aria-label="Download all images"
                    >
                      <Image className="w-4 h-4" />
                      Download Images
                    </button>
                  )}
                </div>
                {storyData.generatedImages.some((img) => img) && (
                  <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      <strong>Note:</strong> Due to browser security restrictions, images may not embed in the PDF. Use the HTML download for best results, or download images separately and add them to the PDF manually using a PDF editor (e.g., Adobe Acrobat).
                    </p>
                  </div>
                )}
                <button
                  onClick={() => {
                    setStoryData({
                      storyType: '',
                      genre: '',
                      customGenre: '',
                      mainCharacter: '',
                      characterDetails: {
                        name: '',
                        age: '',
                        ethnicity: '',
                        appearance: '',
                        personality: '',
                        referenceImage: null,
                        referenceImageUrl: '',
                        imageAppearance: '', // Keep this field
                      },
                      setting: '',
                      customSetting: '',
                      environmentDetails: '',
                      plotElements: [],
                      customPlotElements: '',
                      tone: '',
                      bookSize: 'short',
                      chapters: 3,
                      wordsPerChapter: 500,
                      totalWords: 1500,
                      generateImages: true,
                      imageOrientation: 'square',
                      imageSize: '1024x1024',
                      imageStyle: 'colorful',
                      imageQuality: 'standard',
                      pdfPageSize: 'letter',
                      generatedStory: null,
                      generatedImages: [],
                      generatedChapters: [],
                      chapterSummaries: [],
                      sdModel: 'sdxl',
                      estimatedCost: 0,
                      model: 'gpt-4o-mini',
                    });
                    setCurrentStep(1);
                    setGenerationProgress('');
                  }}
                  className="mt-4 w-full py-2 bg-gray-200 rounded hover:bg-gray-300"
                  aria-label="Create another story"
                >
                  Create Another Story
                </button>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-6 mb-4">
          {/* ... header content ... */}

          {/* Update the step indicator */}
          <div className="flex items-center justify-between mb-8 overflow-x-auto">
            {steps.map((step, index) => (
              <div key={index} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${index <= currentStep ? 'bg-purple-500 text-white' : 'bg-gray-200 text-gray-500'
                    }`}
                  aria-label={`Step ${index + 1}: ${step}`}
                >
                  {index + 1}
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`h-1 w-8 mx-1 ${index < currentStep ? 'bg-purple-500' : 'bg-gray-200'
                      }`}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="mb-8">{renderStep()}</div>

          {/* Update the navigation buttons */}
          <div className="flex justify-between">
            <button
              onClick={() => setCurrentStep(currentStep - 1)}
              disabled={currentStep === 0}
              className="flex items-center gap-2 px-6 py-2 bg-gray-200 rounded-lg disabled:opacity-50 hover:bg-gray-300"
              aria-label="Go to previous step"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
            {currentStep < steps.length - 1 && (
              <button
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={
                  // Update the validation logic (remove apiKey check, adjust case numbers)
                  (currentStep === 0 && !storyData.storyType) ||     // was case 1
                  (currentStep === 1 && !storyData.genre) ||         // was case 2
                  (currentStep === 2 && !storyData.mainCharacter) || // was case 3
                  (currentStep === 3 && !storyData.setting) ||       // was case 4
                  (currentStep === 5 && !storyData.tone)             // was case 6
                }
                className="flex items-center gap-2 px-6 py-2 bg-purple-500 text-white rounded-lg disabled:opacity-50 hover:bg-purple-600"
                aria-label="Go to next step"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoryWizardApp;